/**
 * Utility functions for report generation
 */

export type TimeRange = 
  | "last24hours" 
  | "last7days" 
  | "last30days" 
  | "last90days" 
  | "lastYear" 
  | "custom";

/**
 * Convert a time range string to date objects
 */
export function timeRangeToDates(timeRange: TimeRange, customFrom?: Date, customTo?: Date): { from: Date; to: Date } {
  const to = new Date();
  let from: Date;

  switch (timeRange) {
    case "last24hours":
      from = new Date(to.getTime() - 24 * 60 * 60 * 1000);
      break;
    case "last7days":
      from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case "last30days":
      from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case "last90days":
      from = new Date(to.getTime() - 90 * 24 * 60 * 60 * 1000);
      break;
    case "lastYear":
      from = new Date(to.getTime() - 365 * 24 * 60 * 60 * 1000);
      break;
    case "custom":
      if (customFrom && customTo) {
        from = customFrom;
        return { from, to: customTo };
      }
      // Fallback to 7 days if custom dates not provided
      from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    default:
      from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
  }

  return { from, to };
}

/**
 * Get display label for time range
 */
export function getTimeRangeLabel(timeRange: TimeRange): string {
  switch (timeRange) {
    case "last24hours": return "Last 24 Hours";
    case "last7days": return "Last 7 Days";
    case "last30days": return "Last 30 Days";
    case "last90days": return "Last 90 Days";
    case "lastYear": return "Last Year";
    case "custom": return "Custom Range";
    default: return "Selected Period";
  }
}

/**
 * Download a blob as a file
 */
export function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Generate PDF directly from report data
 */
export async function generatePdfFromReportData(
  reportData: {
    title: string;
    summary: any;
    data: any[];
    period?: { from: Date | string; to: Date | string };
    metadata?: any;
    reportType?: string;
  },
  filename: string = 'report.pdf'
) {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = 20;

  // Add title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(reportData.title || 'Report', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15;

  // Add metadata
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  if (reportData.period) {
    const from = reportData.period.from instanceof Date 
      ? reportData.period.from.toLocaleDateString() 
      : new Date(reportData.period.from).toLocaleDateString();
    const to = reportData.period.to instanceof Date 
      ? reportData.period.to.toLocaleDateString() 
      : new Date(reportData.period.to).toLocaleDateString();
    doc.text(`Period: ${from} to ${to}`, 14, yPosition);
    yPosition += 7;
  }
  
  if (reportData.metadata?.generatedAt) {
    const generatedAt = new Date(reportData.metadata.generatedAt).toLocaleString();
    doc.text(`Generated: ${generatedAt}`, 14, yPosition);
    yPosition += 10;
  }

  // Add summary section
  if (reportData.summary && Object.keys(reportData.summary).length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary', 14, yPosition);
    yPosition += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const summaryEntries = Object.entries(reportData.summary);
    summaryEntries.forEach(([key, value]) => {
      if (yPosition > pageHeight - 30) {
        doc.addPage();
        yPosition = 20;
      }
      const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
      const val = typeof value === 'object' ? JSON.stringify(value) : String(value);
      doc.text(`${label}: ${val}`, 20, yPosition);
      yPosition += 7;
    });
    yPosition += 5;
  }

  // Add data table
  if (reportData.data && reportData.data.length > 0) {
    // Determine columns based on report type
    let columns: string[] = [];
    let rows: any[] = [];

    if (reportData.reportType === 'availability' || reportData.data[0]?.deviceName) {
      columns = ['Device Name', 'Device Type', 'Group', 'Availability %', 'Uptime (h)', 'Downtime (h)'];
      rows = reportData.data.map((item: any) => [
        item.deviceName || item.name || 'Unknown',
        item.deviceType || item.type || 'N/A',
        item.deviceGroup || item.group || 'N/A',
        (item.availabilityPercentage || item.value || 0).toFixed(2) + '%',
        (item.uptimeHours || 0).toFixed(2),
        (item.downtimeHours || 0).toFixed(2)
      ]);
    } else if (reportData.data[0]?.averageCpuUtilizationPct !== undefined) {
      // Performance report
      columns = ['Device', 'CPU %', 'Memory %', 'Network (Mbps)', 'Status'];
      rows = reportData.data.map((item: any) => [
        item.deviceName || 'Unknown',
        (item.averageCpuUtilizationPct || 0).toFixed(1),
        (item.averageMemoryUsagePct || 0).toFixed(1),
        (item.averageNetworkThroughputMbps || 0).toFixed(1),
        item.deviceStatus || 'Normal'
      ]);
    } else if (reportData.data[0]?.severity !== undefined) {
      // Incidents report
      columns = ['Device', 'Severity', 'Issue', 'Time', 'Status'];
      rows = reportData.data.map((item: any) => [
        item.deviceName || 'Unknown',
        item.severity || 'Info',
        (item.issueDescription || item.message || 'N/A').substring(0, 50),
        item.timestamp ? new Date(item.timestamp).toLocaleDateString() : 'N/A',
        item.resolutionStatus || (item.resolved ? 'Resolved' : 'Open')
      ]);
    } else if (reportData.data[0]?.currentUtilizationPct !== undefined) {
      // Capacity report
      columns = ['Device', 'Current %', 'Projected (3m) %', 'Threshold %', 'Action'];
      rows = reportData.data.map((item: any) => [
        item.deviceName || 'Unknown',
        (item.currentUtilizationPct || 0).toFixed(1),
        (item.projectedUtilization3mPct || 0).toFixed(1),
        (item.thresholdPercentage || 80) + '%',
        item.actionRequired || 'None'
      ]);
    } else if (reportData.data[0]?.serviceOrDeviceName !== undefined) {
      // SLA report
      columns = ['Service/Device', 'Availability %', 'Target %', 'Response Time (s)', 'Status'];
      rows = reportData.data.map((item: any) => [
        item.serviceOrDeviceName || 'Unknown',
        (item.availabilityPercentage || 0).toFixed(2) + '%',
        (item.targetAvailabilityPercentage || 99.9).toFixed(2) + '%',
        (item.responseTimeSeconds || 0).toFixed(2),
        item.slaStatus || 'Not Met'
      ]);
    } else {
      // Generic fallback - use all keys from first item
      const firstItem = reportData.data[0];
      if (firstItem) {
        columns = Object.keys(firstItem).slice(0, 6); // Limit to 6 columns
        rows = reportData.data.map((item: any) => 
          columns.map(col => {
            const val = item[col];
            if (val instanceof Date) return val.toLocaleDateString();
            if (typeof val === 'object') return JSON.stringify(val).substring(0, 30);
            return String(val || 'N/A');
          })
        );
      }
    }

    if (columns.length > 0 && rows.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Data', 14, yPosition);
      yPosition += 10;

      autoTable(doc, {
        head: [columns],
        body: rows,
        startY: yPosition,
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { left: 14, right: 14 },
        didDrawPage: (data: any) => {
          // Add page numbers
          doc.setFontSize(10);
          doc.text(
            `Page ${data.pageNumber}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );
        }
      });
    }
  }

  // Save the PDF
  doc.save(filename);
}

/**
 * Format HTML content for better PDF output
 */
function formatHtmlForPdf(htmlContent: string): string {
  // Extract body content from HTML if it's a full HTML document
  let bodyContent = htmlContent;
  const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  if (bodyMatch) {
    bodyContent = bodyMatch[1];
  }
  
  // Create a well-formatted HTML document
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Report</title>
  <style>
    @media print {
      @page {
        margin: 1.5cm;
        size: A4;
      }
      body {
        margin: 0;
        font-size: 12pt;
      }
      table {
        page-break-inside: auto;
      }
      tr {
        page-break-inside: avoid;
        page-break-after: auto;
      }
      thead {
        display: table-header-group;
      }
      tfoot {
        display: table-footer-group;
      }
    }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      margin: 20px;
      color: #333;
      line-height: 1.6;
    }
    h1 {
      color: #1f2937;
      border-bottom: 3px solid #3b82f6;
      padding-bottom: 10px;
      margin-bottom: 20px;
    }
    h2 {
      color: #374151;
      margin-top: 30px;
      margin-bottom: 15px;
      border-bottom: 2px solid #e5e7eb;
      padding-bottom: 5px;
    }
    h3 {
      color: #4b5563;
      margin-top: 20px;
      margin-bottom: 10px;
    }
    table {
      border-collapse: collapse;
      width: 100%;
      margin: 20px 0;
      font-size: 11pt;
    }
    th, td {
      border: 1px solid #d1d5db;
      padding: 10px;
      text-align: left;
    }
    th {
      background-color: #f3f4f6;
      font-weight: 600;
      color: #111827;
    }
    tr:nth-child(even) {
      background-color: #f9fafb;
    }
    pre {
      background-color: #f3f4f6;
      padding: 15px;
      border-radius: 5px;
      overflow-x: auto;
      font-size: 10pt;
      border: 1px solid #e5e7eb;
    }
    .summary-box {
      background-color: #eff6ff;
      border-left: 4px solid #3b82f6;
      padding: 15px;
      margin: 20px 0;
      border-radius: 4px;
    }
    .metadata {
      display: flex;
      flex-wrap: wrap;
      gap: 15px;
      margin: 20px 0;
      padding: 15px;
      background-color: #f9fafb;
      border-radius: 5px;
    }
    .metadata-item {
      flex: 1;
      min-width: 200px;
    }
    .metadata-label {
      font-weight: 600;
      color: #6b7280;
      font-size: 10pt;
      margin-bottom: 5px;
    }
    .metadata-value {
      color: #111827;
      font-size: 11pt;
    }
  </style>
</head>
<body>
  ${bodyContent}
</body>
</html>`;
}

/**
 * Convert HTML to PDF (client-side using browser print)
 * Opens print dialog where user can save as PDF
 */
export function htmlToPdf(htmlContent: string, filename: string = 'report.pdf') {
  // Format HTML for better PDF output
  const formattedHtml = formatHtmlForPdf(htmlContent);
  
  // Create a blob URL for the HTML content
  const htmlBlob = new Blob([formattedHtml], { type: 'text/html' });
  const url = URL.createObjectURL(htmlBlob);
  
  // Open in new window
  const printWindow = window.open(url, '_blank');
  if (!printWindow) {
    alert('Please allow popups to generate PDF. You can also download the HTML file and open it in your browser to print.');
    // Fallback: download as HTML
    downloadBlob(htmlBlob, filename.replace('.pdf', '.html'));
    URL.revokeObjectURL(url);
    return;
  }

  // Wait for window to load, then trigger print
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
      // Clean up URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  };
  
  // Fallback if onload doesn't fire
  setTimeout(() => {
    if (printWindow.document.readyState === 'complete') {
      printWindow.print();
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    }
  }, 1000);
}

